export interface SeoMetadata {
  meta_title?: string;
  meta_description?: string;
  keywords?: string[];
  canonical_url?: string;
}

// Raw API response (no WordPress format)
export interface RawBlogPost {
  _id: string;
  title: string;
  slug: string;
  content?: string | string[];
  excerpt?: string;
  featured_image?: { url?: string; alt?: string; caption?: string };
  created_at?: string;
  updated_at?: string;
  published_at?: string;
  content_type?: string;
  status?: string;
  seo?: SeoMetadata;
  [key: string]: unknown;
}

// WordPress API response types for blog posts
export interface WPBlogPost {
  id: number | string;
  date: string;
  date_gmt: string;
  guid: {
    rendered: string;
  };
  modified: string;
  modified_gmt: string;
  slug: string;
  status: string;
  type: string;
  link: string;
  published_at?: string;
  content_type?: string;
  title?: {
    rendered: string;
  };
  content?: {
    rendered: string;
    protected?: boolean;
  };
  excerpt?: {
    rendered: string;
    protected?: boolean;
  };
  author: number;
  featured_media: number;
  comment_status: string;
  ping_status: string;
  sticky: boolean;
  template: string;
  format: string;
  meta: {
    footnotes: string;
  };
  // meta: unknown[];
  categories: number[];
  tags: number[];
  acf: unknown[];
  _links: {
    self: Array<{ href: string }>;
    collection: Array<{ href: string }>;
    about?: Array<{ href: string }>;
    author?: Array<{ embeddable: boolean; href: string }>;
    replies?: Array<{ embeddable: boolean; href: string }>;
    "version-history"?: Array<{ count: number; href: string }>;
    "predecessor-version"?: Array<{ id: number; href: string }>;
    "wp:featuredmedia"?: Array<{ embeddable: boolean; href: string }>;
    "wp:attachment"?: Array<{ href: string }>;
    "wp:term"?: Array<{ taxonomy: string; embeddable: boolean; href: string }>;
    curies?: Array<{ name: string; href: string; templated: boolean }>;
  };
  _embedded: {
    "wp:featuredmedia": Array<{ source_url: string }>;
  };
  yoast_head_json?: {
    title?: string;
    description?: string;
    canonical?: string;
  };


}

// Transformed blog post type for use in components (compatible with existing BlogCard)
export interface BlogPost {
  id: number | string;
  title: string;
  category: string;
  date: string;
  excerpt: string;
  image: {
    blogImage: string;
    desktop: string;
    mobile: string;
    alt: string;
  };
  link: string;
  tag?: string;
  content?: string;
  slug: string;
  published_at: string;
  content_type: string;
  seo?: SeoMetadata;

}

// Format date to match existing format (e.g., "October 15, 2023")
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const stripHtml = (html: string): string => {
  return html.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, '');
};

// Utility function to transform WordPress API response to BlogPost format
export const transformWPBlogPost = (wpPost: WPBlogPost): BlogPost => {
  const getPostCategory = (categories: number[]): string => {
    if (categories.includes(2196)) return 'News';
    if (categories.includes(2197)) return 'Blog';
    return 'Blog';
  };
  const title = wpPost.title?.rendered || 'Untitled';
  const content = wpPost.content?.rendered || '';
  const excerpt = wpPost.excerpt?.rendered || '';

  return {
    id: wpPost.id,
    title,
    category: getPostCategory(wpPost.categories || []),
    date: formatDate(wpPost.date),
    excerpt: stripHtml(excerpt),
    image: {
      blogImage: wpPost._embedded?.["wp:featuredmedia"]?.[0]?.source_url || '/images/blog-main-banner-dummy.webp',
      desktop: '/images/blog-main-banner-dummy.webp',
      mobile: '/images/blog-main-banner-dummy.webp',
      alt: title,
    },
    link: `/blog/${wpPost.slug}`,
    tag: 'New',
    content,
    slug: wpPost.slug,
    published_at: formatDate(wpPost.published_at || wpPost.date),
    content_type: wpPost.content_type || 'Blog',
    seo: {
      meta_title: wpPost.yoast_head_json?.title,
      meta_description: wpPost.yoast_head_json?.description,
      canonical_url: wpPost.yoast_head_json?.canonical
    }
  };
};

/** Normalize API response to BlogPost (supports both raw and WordPress format). */
export function normalizeBlogPost(post: RawBlogPost | WPBlogPost): BlogPost {
  const isRaw = (p: RawBlogPost | WPBlogPost): p is RawBlogPost =>
    '_id' in p && typeof (p as RawBlogPost).title === 'string';

  if (isRaw(post)) {
    const content = Array.isArray(post.content)
      ? post.content.join('')
      : (post.content ?? '');
    const title = post.title || 'Untitled';
    const dateStr = post.published_at || post.created_at || post.updated_at || '';
    return {
      id: post._id,
      title,
      category: 'Blog',
      date: dateStr ? formatDate(dateStr) : '',
      excerpt: stripHtml(post.excerpt ?? ''),
      image: {
        blogImage: post.featured_image?.url || '/images/blog-main-banner-dummy.webp',
        desktop: '/images/blog-main-banner-dummy.webp',
        mobile: '/images/blog-main-banner-dummy.webp',
        alt: post.featured_image?.alt ?? title,
      },
      link: `/blog/${post.slug}`,
      tag: 'New',
      content,
      slug: post.slug,
      published_at: dateStr ? formatDate(dateStr) : '',
      content_type: post.content_type || 'Blog',
      seo: post.seo,
    };
  }
  return transformWPBlogPost(post);
} 